/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react'
import { Button } from 'reactstrap';
import { UniversalTrackerBlueprintMin, UniversalTrackerMin, UniversalTrackerPlain } from '../../types/universalTracker';
import { getGroup, standards } from '@g17eco/core';
import Table from '../table/Table';
import SimpleTooltip from '../simple-tooltip';
import { NameCell } from '../custom-metrics/partials/NameCell';
import { UtrTableSearch } from './UtrTableSearch';
import './UtrTableEdit.scss';
import IconButton from '../button/IconButton';

interface UtrTableEditProps {
  questions: UniversalTrackerMin[];
  currentQuestions: UniversalTrackerPlain[];
  itemName?: string;
  showHeader?: boolean;
  searchText: string;
  handleSearch: (search: string) => void;
  addToGroup: (utrId: string) => Promise<void>;
  removeFromGroup: (utrId: string) => Promise<void>;
  isUTRInGroup: (utrId: string) => boolean;
  errorMessage: string;
  isSaving: boolean;
}

export const UtrTableEdit = (props: UtrTableEditProps) => {
  const {
    questions,
    currentQuestions,
    searchText,
    handleSearch,
    addToGroup,
    removeFromGroup,
    isUTRInGroup,
    errorMessage,
    isSaving,
    itemName = 'Metric',
    showHeader = true
  } = props;

  const [showSearchForm, setShowSearchForm] = React.useState(false);

  const getBadge = (text: string, tooltip: string, badgeColour: string, key: string) => {
    return (
        <SimpleTooltip text={tooltip} key={key}>
          <div className={`badge ${badgeColour}`}>{text}</div>
        </SimpleTooltip>
    );
  };

  const tableColumns: any[] = [
    {
      accessor: '_id',
      Header: () => '',
      Cell: ({ value }: { value: string }) => {
        if (isUTRInGroup(value)) {
          return (
            <IconButton tooltip='Remove' disabled={isSaving} color='danger' onClick={() => removeFromGroup(value)} icon='fa-times' />
          );
        }
        return (
          <IconButton tooltip='Add' disabled={isSaving} onClick={() => addToGroup(value)} icon='fa-plus' />
        )
      },
      cellProps: {
        className: 'idCol'
      }
    },
    {
      accessor: 'name',
      Header: () => 'Name',
      cellProps: {
        className: 'nameCol'
      },
      Cell: ({ row }: { row: { values: UniversalTrackerPlain } }) => <NameCell
        isInGroup={isUTRInGroup(row.values._id)} row={row} />
    },
    {
      accessor: 'type',
      Header: () => 'Type',
      Cell: ({ row }: { row: { original: UniversalTrackerBlueprintMin, values: UniversalTrackerPlain } }) => {
        const value = row.values.type;
        let valueStr = value;
        if (value === 'custom_kpi') {
          valueStr = 'Custom Metric';
        }
        if (value === 'metric') {
          valueStr = 'Standardized Metric';
        }

        // @todo move badges to be shared with MetricTable
        const selectedBadgeColour = value === 'custom_kpi' ? 'background-ThemeAccentMedium text-ThemeTextWhite' : 'background-ThemeAccentExtralight';
        const unselectedBadgeColour = value === 'custom_kpi' ? 'background-ThemeTextPlaceholder text-ThemeTextWhite' : 'background-ThemeNeutralsLight';
        const badgeColour = isUTRInGroup(row.values._id) ? selectedBadgeColour : unselectedBadgeColour;

        const text = standards[valueStr]?.shortName ?? valueStr;
        const tooltip = standards[valueStr]?.name ?? valueStr
        const badges = [
          getBadge(text, tooltip, badgeColour, `${row.original._id}-${row.values.type}`)
        ];

        if (row.original.alternatives) {
          for (const [key] of Object.entries(row.original.alternatives)) {
            const group = getGroup('standards', key);
            if (group && key !== row.values.type) {
              badges.push(getBadge(group.shortName ?? group.name, group.name, badgeColour, `${row.original._id}-${key}`));
            }
          }
        }

        return <div className='text-nowrap'>{badges}</div>
      },
      cellProps: {
        className: 'typeCol',
      },
      headerProps: {
        style: { width: '100px' }
      },
    },
  ];

  const setSearchText = (e: React.ChangeEvent<any>) => {
    handleSearch(e.target.value);
  }

  const resetForms = () => {
    setShowSearchForm(false);
    handleSearch('');
  };

  const tableQuestions = showSearchForm ? questions : currentQuestions;
  return (
    <div className={'utr-table-edit-container pt-3 border-top'}>

      <Button color='primary' outline={showSearchForm} onClick={resetForms} className='p-2 mr-3'>Manage Metrics</Button>
      <Button color='primary' outline={!showSearchForm} onClick={() => setShowSearchForm(true)} className='p-2'>Add a new Metric</Button>
      {showSearchForm &&
        <>
          <h5 className='mt-3 title-container'>
            <span><i className='fa fa-search mr-2' />Search for a {itemName}</span>
          </h5>
          <div className='mt-3'>
            <UtrTableSearch
              label={`Search for an existing ${itemName}`}
              filterMessage={`Add the ${itemName}`}
              acceptMessage={`Accept changes and go back to the ${itemName} list`}
              handleCancel={resetForms}
              onChange={setSearchText}
              value={searchText}
              showConfirmationButton={showHeader}
            />
          </div>
        </>
      }
      {errorMessage && <div className='alert alert-danger'>{errorMessage}</div>}
        <div className='mt-3 metric-table-container'>
          {
            tableQuestions.length === 0
              ?
              <div className='alert alert-primary'>You have no metrics currently selected</div>
              :
              (
                <Table className={isSaving ? 'isSaving' : ''}
                  columns={tableColumns}
                  data={tableQuestions}
                  pageSize={10} />
              )
          }
        </div>
    </div>
  )
}
